import { useState, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Input, Textarea } from '@creanizer/ui'
import { Info, Building, Users, Star, BookOpen } from 'lucide-react'
import type { SocietyResult, SocietyChangeInfo } from '@creanizer/types'

interface ChangeHighlight {
  isUpdated: (fieldPath: string) => boolean
  isAdded: (fieldPath: string) => boolean
  isMemberAdded: (index: number) => boolean
  isEpisodeAdded: (index: number) => boolean
  getFieldClassName: (fieldPath: string) => string
  getFieldBadge: (fieldPath: string) => React.ReactNode
}

// エピソードタイプの日本語マッピング
const episodeTypeNames: Record<string, string> = {
  founding: '創設',
  success: '成功',
  failure: '失敗',
  legend: '伝説',
  turning_point: '転機',
  scandal: 'スキャンダル',
  heroic: '英雄譚',
  mysterious: '謎',
  rivalry: '対決',
  discovery: '発見',
  crisis: '危機',
  celebration: '祝祭',
  betrayal: '裏切り',
  alliance: '同盟',
  innovation: '革新',
  tradition: '伝統',
  conflict: '対立',
  reconciliation: '和解',
  expansion: '拡大',
  decline: '衰退',
  rebirth: '再生',
  sacrifice: '犠牲',
  revelation: '暴露',
  transformation: '変革',
  miracle: '奇跡',
  accident: '事故',
  meeting: '出会い',
  departure: '別離',
  return: '帰還',
  trial: '試練'
}

// エピソードタイプのバッジバリアント
const getEpisodeBadgeVariant = (type: string): "default" | "secondary" | "destructive" | "outline" => {
  const destructiveTypes = ['failure', 'scandal', 'betrayal', 'conflict', 'decline', 'accident']
  const secondaryTypes = ['success', 'heroic', 'alliance', 'innovation', 'expansion', 'rebirth', 'miracle']
  const defaultTypes = ['founding', 'turning_point', 'meeting', 'return', 'transformation']
  
  if (destructiveTypes.includes(type)) return 'destructive'
  if (secondaryTypes.includes(type)) return 'secondary'
  if (defaultTypes.includes(type)) return 'default'
  return 'outline'
}

interface SocietyResultTabsProps {
  result: SocietyResult
  isEditing?: boolean
  onEdit?: (updatedResult: Partial<SocietyResult>) => void
  changeHighlight?: ChangeHighlight
}

// 編集可能なフィールドコンポーネント
function EditableField({
  label,
  value,
  fieldPath,
  isEditing,
  onEdit,
  changeHighlight,
  multiline = false
}: {
  label: string
  value: string | undefined
  fieldPath: string
  isEditing: boolean
  onEdit?: (value: string) => void
  changeHighlight?: ChangeHighlight
  multiline?: boolean
}) {
  const [localValue, setLocalValue] = useState(value || '')

  useEffect(() => {
    setLocalValue(value || '')
  }, [value])

  const handleBlur = () => {
    if (onEdit && localValue !== value) {
      onEdit(localValue)
    }
  }

  const className = changeHighlight?.getFieldClassName(fieldPath) || ''
  const badge = changeHighlight?.getFieldBadge(fieldPath)

  if (isEditing && onEdit) {
    return (
      <div className={className}>
        <h4 className="font-semibold mb-1 flex items-center">
          {label}
          {badge}
        </h4>
        {multiline ? (
          <Textarea
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            className="min-h-[100px]"
          />
        ) : (
          <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
          />
        )}
      </div>
    )
  }

  if (!value) return null

  return (
    <div className={className}>
      <h4 className="font-semibold mb-1 flex items-center">
        {label}
        {badge}
      </h4>
      <p className={multiline ? 'whitespace-pre-wrap' : ''}>{value}</p>
    </div>
  )
}

export function SocietyResultTabs({ result, isEditing = false, onEdit, changeHighlight }: SocietyResultTabsProps) {
  // 編集ハンドラ
  const handleBasicInfoEdit = (field: string, value: string) => {
    if (!onEdit) return
    onEdit({
      basicInfo: {
        ...result.basicInfo,
        [field]: value
      }
    })
  }

  const handleStructureEdit = (field: string, value: string) => {
    if (!onEdit) return
    onEdit({
      structure: {
        ...result.structure,
        [field]: value
      }
    })
  }

  const handleFeaturesEdit = (field: string, value: string) => {
    if (!onEdit) return
    onEdit({
      features: {
        ...result.features,
        [field]: value
      }
    })
  }

  const handleMemberEdit = (index: number, field: string, value: string) => {
    if (!onEdit || !result.members?.importantMembers) return
    const newMembers = [...result.members.importantMembers]
    newMembers[index] = { ...newMembers[index], [field]: value }
    onEdit({
      members: {
        ...result.members,
        importantMembers: newMembers
      }
    })
  }
  return (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid grid-cols-5 gap-1">
        <TabsTrigger value="basic" className="text-xs">
          <Info className="h-3 w-3 mr-1" />
          基本
        </TabsTrigger>
        <TabsTrigger value="structure" className="text-xs">
          <Building className="h-3 w-3 mr-1" />
          構造
        </TabsTrigger>
        <TabsTrigger value="members" className="text-xs">
          <Users className="h-3 w-3 mr-1" />
          メンバー
        </TabsTrigger>
        <TabsTrigger value="features" className="text-xs">
          <Star className="h-3 w-3 mr-1" />
          特徴
        </TabsTrigger>
        <TabsTrigger value="episodes" className="text-xs">
          <BookOpen className="h-3 w-3 mr-1" />
          逸話
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="basic" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>基本情報</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <EditableField
              label="組織名"
              value={result.basicInfo.name}
              fieldPath="basicInfo.name"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('name', v)}
              changeHighlight={changeHighlight}
            />
            {result.basicInfo.aliases && result.basicInfo.aliases.length > 0 && (
              <p className="text-sm text-muted-foreground">
                別名: {result.basicInfo.aliases.join('、')}
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <EditableField
                label="タイプ"
                value={result.basicInfo.type}
                fieldPath="basicInfo.type"
                isEditing={isEditing}
                onEdit={(v) => handleBasicInfoEdit('type', v)}
                changeHighlight={changeHighlight}
              />
              <EditableField
                label="規模"
                value={result.basicInfo.scale}
                fieldPath="basicInfo.scale"
                isEditing={isEditing}
                onEdit={(v) => handleBasicInfoEdit('scale', v)}
                changeHighlight={changeHighlight}
              />
            </div>

            <EditableField
              label="設立時期"
              value={result.basicInfo.foundingDate}
              fieldPath="basicInfo.foundingDate"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('foundingDate', v)}
              changeHighlight={changeHighlight}
            />

            <EditableField
              label="モットー"
              value={result.basicInfo.motto}
              fieldPath="basicInfo.motto"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('motto', v)}
              changeHighlight={changeHighlight}
            />

            <EditableField
              label="シンボル・紋章"
              value={result.basicInfo.symbol}
              fieldPath="basicInfo.symbol"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('symbol', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="概要"
              value={result.basicInfo.summary}
              fieldPath="basicInfo.summary"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('summary', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="詳細説明"
              value={result.basicInfo.description}
              fieldPath="basicInfo.description"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('description', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="目的・理念"
              value={result.basicInfo.purpose}
              fieldPath="basicInfo.purpose"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('purpose', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="公的イメージ"
              value={result.basicInfo.publicImage}
              fieldPath="basicInfo.publicImage"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('publicImage', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="実際の性質"
              value={result.basicInfo.actualNature}
              fieldPath="basicInfo.actualNature"
              isEditing={isEditing}
              onEdit={(v) => handleBasicInfoEdit('actualNature', v)}
              changeHighlight={changeHighlight}
              multiline
            />
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="structure" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>組織構造</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <EditableField
              label="メンバー数"
              value={result.structure?.memberCount}
              fieldPath="structure.memberCount"
              isEditing={isEditing}
              onEdit={(v) => handleStructureEdit('memberCount', v)}
              changeHighlight={changeHighlight}
            />
            
            {Array.isArray(result.structure?.hierarchy) && result.structure.hierarchy.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">階級制度</h4>
                <div className="space-y-3">
                  {result.structure.hierarchy.map((level, index) => {
                    const totalLevels = result.structure.hierarchy.length
                    const displayLevel = totalLevels - index
                    return (
                      <div key={index} className="border-l-2 border-primary pl-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">Lv.{displayLevel}</Badge>
                          <span className="font-medium">{level.title || '不明'}</span>
                          <span className="text-sm text-muted-foreground">({level.count || '不明'})</span>
                        </div>
                        <p className="text-sm">{level.responsibilities || '詳細不明'}</p>
                        {level.requirements && (
                          <p className="text-sm text-muted-foreground mt-1">
                            要件: {level.requirements}
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            
            {Array.isArray(result.structure?.departments) && result.structure.departments.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">部門</h4>
                <div className="grid gap-3">
                  {result.structure.departments.map((dept, index) => (
                    <div key={index} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start mb-1">
                        <h5 className="font-medium">{dept.name}</h5>
                        <Badge variant="secondary">{dept.size}</Badge>
                      </div>
                      <p className="text-sm mb-2">{dept.purpose}</p>
                      {dept.leader && (
                        <p className="text-sm text-muted-foreground">
                          リーダー: {dept.leader}
                        </p>
                      )}
                      {dept.specialties && dept.specialties.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {dept.specialties.map((spec, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <EditableField
              label="新規メンバー獲得方法"
              value={result.structure?.recruitmentMethod}
              fieldPath="structure.recruitmentMethod"
              isEditing={isEditing}
              onEdit={(v) => handleStructureEdit('recruitmentMethod', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="昇進システム"
              value={result.structure?.promotionSystem}
              fieldPath="structure.promotionSystem"
              isEditing={isEditing}
              onEdit={(v) => handleStructureEdit('promotionSystem', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="意思決定プロセス"
              value={result.structure?.decisionMaking}
              fieldPath="structure.decisionMaking"
              isEditing={isEditing}
              onEdit={(v) => handleStructureEdit('decisionMaking', v)}
              changeHighlight={changeHighlight}
              multiline
            />

            <EditableField
              label="内部連絡方法"
              value={result.structure?.communicationMethod}
              fieldPath="structure.communicationMethod"
              isEditing={isEditing}
              onEdit={(v) => handleStructureEdit('communicationMethod', v)}
              changeHighlight={changeHighlight}
              multiline
            />
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="members" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>重要メンバー</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.isArray(result.members?.importantMembers) && result.members.importantMembers.length > 0 && (
              <div className="space-y-4">
                {result.members.importantMembers.map((member, index) => {
                  const isNew = changeHighlight?.isMemberAdded(index)
                  const isUpdated = changeHighlight?.isUpdated(`members.importantMembers.${index}`)
                  const highlightClass = isNew
                    ? 'bg-green-50 dark:bg-green-950 border-green-500'
                    : isUpdated
                    ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-500'
                    : ''

                  return (
                    <div key={index} className={`border rounded-lg p-4 ${highlightClass}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          {isEditing && onEdit ? (
                            <>
                              <Input
                                value={member.name}
                                onChange={(e) => handleMemberEdit(index, 'name', e.target.value)}
                                className="font-semibold text-lg mb-1"
                                placeholder="名前"
                              />
                              <Input
                                value={member.role}
                                onChange={(e) => handleMemberEdit(index, 'role', e.target.value)}
                                className="text-sm"
                                placeholder="役職"
                              />
                            </>
                          ) : (
                            <>
                              <h4 className="font-semibold text-lg">{member.name}</h4>
                              <p className="text-sm text-muted-foreground">{member.role}</p>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isNew && <span className="px-1.5 py-0.5 text-xs bg-green-500 text-white rounded">NEW</span>}
                          {isUpdated && <span className="px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded">更新</span>}
                          <Badge variant="default">#{index + 1}</Badge>
                        </div>
                      </div>
                      {isEditing && onEdit ? (
                        <Textarea
                          value={member.description}
                          onChange={(e) => handleMemberEdit(index, 'description', e.target.value)}
                          className="text-sm min-h-[100px]"
                          placeholder="説明"
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{member.description}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <EditableField
              label="備考"
              value={result.members?.notes}
              fieldPath="members.notes"
              isEditing={isEditing}
              onEdit={(v) => {
                if (!onEdit) return
                onEdit({
                  members: {
                    ...result.members,
                    importantMembers: result.members?.importantMembers || [],
                    notes: v
                  }
                })
              }}
              changeHighlight={changeHighlight}
              multiline
            />
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="features" className="mt-4">
        <div className="space-y-4">
          <Card className={changeHighlight?.getFieldClassName('features.activities')}>
            <CardHeader>
              <CardTitle className="flex items-center">
                活動内容
                {changeHighlight?.getFieldBadge('features.activities')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing && onEdit ? (
                <Textarea
                  value={result.features?.activities || ''}
                  onChange={(e) => handleFeaturesEdit('activities', e.target.value)}
                  className="min-h-[150px]"
                />
              ) : (
                <p className="whitespace-pre-wrap">{result.features?.activities}</p>
              )}
            </CardContent>
          </Card>

          <Card className={changeHighlight?.getFieldClassName('features.culture')}>
            <CardHeader>
              <CardTitle className="flex items-center">
                組織文化
                {changeHighlight?.getFieldBadge('features.culture')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing && onEdit ? (
                <Textarea
                  value={result.features?.culture || ''}
                  onChange={(e) => handleFeaturesEdit('culture', e.target.value)}
                  className="min-h-[150px]"
                />
              ) : (
                <p className="whitespace-pre-wrap">{result.features?.culture}</p>
              )}
            </CardContent>
          </Card>

          <Card className={changeHighlight?.getFieldClassName('features.relations')}>
            <CardHeader>
              <CardTitle className="flex items-center">
                他組織との関係
                {changeHighlight?.getFieldBadge('features.relations')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing && onEdit ? (
                <Textarea
                  value={result.features?.relations || ''}
                  onChange={(e) => handleFeaturesEdit('relations', e.target.value)}
                  className="min-h-[150px]"
                />
              ) : (
                <p className="whitespace-pre-wrap">{result.features?.relations}</p>
              )}
            </CardContent>
          </Card>

          <Card className={changeHighlight?.getFieldClassName('features.secrets')}>
            <CardHeader>
              <CardTitle className="text-destructive flex items-center">
                秘密・裏設定
                {changeHighlight?.getFieldBadge('features.secrets')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                ※ GM/作者向けの情報です
              </p>
              {isEditing && onEdit ? (
                <Textarea
                  value={result.features?.secrets || ''}
                  onChange={(e) => handleFeaturesEdit('secrets', e.target.value)}
                  className="min-h-[150px]"
                />
              ) : (
                <p className="whitespace-pre-wrap">{result.features?.secrets}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </TabsContent>
      
      <TabsContent value="episodes" className="mt-4">
        <div className="space-y-4">
          {/* データ不完全の警告 */}
          {!result.episodes && (
            <Card className="border-yellow-500">
              <CardHeader>
                <CardTitle className="text-yellow-600">⚠️ データ取得エラー</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  エピソードデータが取得できませんでした。生成時にエラーが発生した可能性があります。
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* エピソードが空の場合 */}
          {result.episodes && (!Array.isArray(result.episodes.episodes) || result.episodes.episodes.length === 0) && (
            <Card className="border-orange-500">
              <CardHeader>
                <CardTitle className="text-orange-600">📝 エピソードなし</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  エピソードが生成されませんでした。再度生成をお試しください。
                </p>
                {process.env.NODE_ENV === 'development' && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer">デバッグ情報</summary>
                    <pre className="text-xs mt-1 p-2 bg-gray-100 rounded overflow-auto">
                      {JSON.stringify(result.episodes, null, 2)}
                    </pre>
                  </details>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* 正常なエピソード表示 */}
          {Array.isArray(result.episodes?.episodes) && result.episodes.episodes.length > 0 && (
            <>
              {/* 部分的にデータが欠損している場合の警告 */}
              {result.episodes.episodes.some(ep => !ep.title || !ep.type || !ep.content) && (
                <Card className="border-yellow-500 mb-4">
                  <CardContent className="py-3">
                    <p className="text-sm text-yellow-600">
                      ⚠️ 一部のエピソードデータが不完全です。表示可能な内容のみ表示しています。
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {result.episodes.episodes.map((episode, index) => {
                const isNew = changeHighlight?.isEpisodeAdded(index)
                const isUpdated = changeHighlight?.isUpdated(`episodes.episodes.${index}`)
                const highlightClass = isNew
                  ? 'bg-green-50 dark:bg-green-950 border-green-500'
                  : isUpdated
                  ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-500'
                  : !episode.content ? 'opacity-50' : ''

                return (
                  <Card key={index} className={highlightClass}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="flex items-center gap-2">
                          {episode.title || `エピソード ${index + 1}`}
                          {isNew && <span className="px-1.5 py-0.5 text-xs bg-green-500 text-white rounded">NEW</span>}
                          {isUpdated && <span className="px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded">更新</span>}
                        </CardTitle>
                        <Badge variant={getEpisodeBadgeVariant(episode.type)}>
                          {episodeTypeNames[episode.type] || episode.type || '不明'}
                        </Badge>
                      </div>
                      {/* 出典と信憑性の表示 */}
                      {(episode.source || episode.reliability) && (
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          {episode.source && (
                            <span>出典: {episode.source}</span>
                          )}
                          {episode.reliability && (
                            <span className={
                              episode.reliability.includes('高') ? 'text-green-600' :
                              episode.reliability.includes('中') ? 'text-yellow-600' :
                              episode.reliability.includes('低') ? 'text-red-600' :
                              'text-gray-600'
                            }>
                              信憑性: {episode.reliability}
                            </span>
                          )}
                        </div>
                      )}
                    </CardHeader>
                    <CardContent>
                      {episode.content ? (
                        <p className="whitespace-pre-wrap">{episode.content}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          （エピソード内容が取得できませんでした）
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </>
          )}
        </div>
      </TabsContent>
    </Tabs>
  )
}